/**
 * Audio Helper Utility
 * Provides robust audio playback with error handling, promise handling,
 * and support for multiple simultaneous playback during rapid scanning
 */

// Keep one queue for all sound types so a bad result cannot be lost behind good sounds.
const soundQueue = [];
let isPlaying = false;
let audioUnlocked = false;

export const unlockAudio = () => {
  if (audioUnlocked) {
    return;
  }

  const audioElements = document.getElementsByClassName('good-audio-element');
  const template = audioElements && audioElements[0];
  if (!template) {
    return;
  }

  const audioEl = template.cloneNode(true);
  audioEl.muted = true;
  audioEl.setAttribute('aria-hidden', 'true');
  audioEl.style.display = 'none';
  document.body.appendChild(audioEl);

  try {
    const playPromise = audioEl.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.then(() => {
        audioUnlocked = true;
        audioEl.pause();
        audioEl.remove();
      }).catch(() => audioEl.remove());
    } else {
      audioUnlocked = true;
      audioEl.pause();
      audioEl.remove();
    }
  } catch (error) {
    audioEl.remove();
  }
};

/**
 * Plays a sound by className with proper error handling
 * @param {string} className - The class name of the audio element
 * @returns {Promise<void>}
 */
export const playSoundByClass = (className) => {
  return new Promise(resolve => {
    soundQueue.push({ className, resolve });
    processSoundQueue();
  });
};

const processSoundQueue = () => {
  if (isPlaying || soundQueue.length === 0) {
    return;
  }

  const nextSound = soundQueue.shift();
  const audioElements = document.getElementsByClassName(nextSound.className);
  const template = audioElements && audioElements[0];

  if (!template) {
    console.warn(`Audio element with class '${nextSound.className}' not found`);
    nextSound.resolve();
    processSoundQueue();
    return;
  }

  // Never reset or reuse the rendered element. A dedicated clone owns this request.
  const audioEl = template.cloneNode(true);
  audioEl.removeAttribute('id');
  audioEl.setAttribute('aria-hidden', 'true');
  audioEl.style.display = 'none';
  document.body.appendChild(audioEl);

  isPlaying = true;
  let settled = false;
  let startPlayback;
  const finish = () => {
    if (settled) {
      return;
    }
    settled = true;
    audioEl.removeEventListener('ended', finish);
    audioEl.removeEventListener('error', finish);
    if (startPlayback) {
      audioEl.removeEventListener('canplaythrough', startPlayback);
    }
    isPlaying = false;
    nextSound.resolve();
    processSoundQueue();
    // Give the browser time to flush the final decoded audio frames before cleanup.
    setTimeout(() => {
      audioEl.pause();
      audioEl.removeAttribute('src');
      audioEl.load();
      audioEl.remove();
    }, 100);
  };

  audioEl.addEventListener('ended', finish, { once: true });
  audioEl.addEventListener('error', finish, { once: true });
  try {
    audioEl.load();
    startPlayback = () => {
      audioEl.removeEventListener('canplaythrough', startPlayback);
      const playPromise = audioEl.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(error => {
          console.warn(`Failed to play sound '${nextSound.className}':`, error.message);
          finish();
        });
      }
    };
    if (audioEl.readyState >= 3) {
      startPlayback();
    } else {
      audioEl.addEventListener('canplaythrough', startPlayback, { once: true });
    }
  } catch (error) {
    console.warn(`Failed to play sound '${nextSound.className}':`, error.message);
    finish();
  }
};

/**
 * Creates multiple audio elements for a sound to support simultaneous playback
 * Useful for rapid scanning where multiple sounds may need to play at once
 * @param {string} className - Base class name for the audio elements
 * @param {string} soundUrl - URL to the sound file
 * @param {number} instanceCount - Number of simultaneous instances to create
 */
export const createMultiAudioElements = (className, soundUrl, instanceCount = 1) => {
  const container = document.getElementById('audio-elements-container');
  
  if (!container) {
    console.warn('Audio elements container not found. Create a div with id="audio-elements-container" in your component.');
    return;
  }

  for (let i = 0; i < instanceCount; i++) {
    const audio = document.createElement('audio');
    audio.className = className;
    
    const source = document.createElement('source');
    source.src = soundUrl;
    source.type = 'audio/mpeg';
    
    audio.appendChild(source);
    container.appendChild(audio);
  }
};

/**
 * Plays a sound with a single function, handling errors silently
 * (doesn't throw, just logs)
 * Automatically cycles through multiple instances for rapid playback
 * @param {string} className - The class name of the audio element
 */
export const playSoundSilent = (className) => {
  playSoundByClass(className)
    .catch(() => {});
};
